import { useState } from 'react';
import { motion } from 'framer-motion';
import { MessageCircle, Mail, Loader2, CheckCircle } from 'lucide-react';
import { submitForm } from '../../lib/api';
import { getWhatsAppLink, getEmailLink, EMAIL } from '../../lib/utils';
import { useLanguage } from '../../context/LanguageContext';

export const ContactSection = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      await submitForm({
        form_type: 'contact',
        name: formData.name,
        email: formData.email,
        message: formData.message
      });
      setIsSuccess(true);
      setFormData({ name: '', email: '', message: '' });
      setTimeout(() => setIsSuccess(false), 3000);
    } catch (err) {
      setError(t('form_error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactMethods = [
    {
      icon: MessageCircle,
      title: t('whatsapp'),
      description: t('whatsapp'),
      action: getWhatsAppLink("Bonjour, je vous contacte depuis le site JoSmart."),
      isExternal: true,
      color: 'bg-[#25D366] hover:bg-[#20BD5A]'
    },
    {
      icon: Mail,
      title: t('email'),
      description: EMAIL,
      action: getEmailLink(),
      isExternal: false,
      color: 'bg-[#4A90D9] hover:bg-[#5BA0E9]'
    }
  ];

  return (
    <section id="contact" className="py-24 bg-[#0B1521]" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-1.5 rounded-full bg-[#4A90D9]/20 text-[#4A90D9] text-sm font-medium tracking-wide mb-4">
            {t('contact_badge')}
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-4">
            {t('contact_title')}
          </h2>
          <p className="text-lg text-white/70 max-w-2xl mx-auto">
            {t('contact_subtitle')}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Methods */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <h3 className="text-xl font-semibold text-white mb-6">{t('contact_methods')}</h3>
            <div className="space-y-4">
              {contactMethods.map((method) => (
                <a
                  key={method.title}
                  href={method.action}
                  target={method.isExternal ? '_blank' : undefined}
                  rel={method.isExternal ? 'noopener noreferrer' : undefined}
                  className={`flex items-center gap-4 p-6 rounded-xl ${method.color} text-white transition-colors duration-200`}
                  data-testid={`contact-method-${method.title.toLowerCase()}`}
                >
                  <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                    <method.icon size={24} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg">{method.title}</h4>
                    <p className="opacity-90">{method.description}</p>
                  </div>
                </a>
              ))}
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <div className="bg-[#152840] rounded-2xl p-8 border border-white/10">
              <h3 className="text-xl font-semibold text-white mb-6">{t('contact_form_title')}</h3>

              {isSuccess ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-8"
                >
                  <CheckCircle size={48} className="mx-auto text-green-500 mb-4" />
                  <p className="text-white font-medium">{t('contact_success')}</p>
                  <p className="text-white/60 text-sm mt-2">{t('contact_success_sub')}</p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-white/80 text-sm mb-2">{t('form_name')} *</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-lg input-dark"
                        placeholder={t('form_name')}
                        data-testid="contact-input-name"
                      />
                    </div>
                    <div>
                      <label className="block text-white/80 text-sm mb-2">{t('form_email')} *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-lg input-dark"
                        placeholder={t('form_email')}
                        data-testid="contact-input-email"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-white/80 text-sm mb-2">{t('form_message')} *</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="w-full px-4 py-3 rounded-lg input-dark resize-none"
                      placeholder={t('form_message')}
                      data-testid="contact-textarea"
                    />
                  </div>

                  {error && (
                    <p className="text-red-400 text-sm">{error}</p>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-3 bg-[#4A90D9] text-white rounded-full font-semibold hover:bg-[#5BA0E9] transition-colors duration-200 flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid="contact-submit-btn"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 size={20} className="animate-spin" />
                        {t('form_sending')}
                      </>
                    ) : (
                      t('form_submit')
                    )}
                  </button>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
